<script lang="ts">
import { launchModal, ModalType } from '@/stores/modalControl'
import { defineComponent, ref } from 'vue'
import VueCal from 'vue-cal'
import 'vue-cal/dist/vuecal.css'

function translateHour(hour: number) {
  var output = ''
  if (hour == 0) {
    return '12:00 AM'
  }

  if (hour < 12) {
    output += hour
    const dotIndex = ('' + hour).indexOf('.')
    if (dotIndex > 0) {
      const minutes = parseInt(('' + hour).substring(dotIndex + 1))
      output += `:${minutes * 60} AM`
    } else {
      output += ':00 AM'
    }
  } else if (hour == 12) {
    return '12:00 PM'
  } else {
    if (Math.floor(hour) - 12 == 0) {
      output += '12'
    } else {
      output += hour - 12
    }

    const dotIndex = ('' + hour).indexOf('.')
    if (dotIndex > 0) {
      const minutes = parseInt(('' + hour).substring(dotIndex + 1))
      output += `:${('' + minutes * 60).slice(0, 2)} PM`
    } else {
      output += ':00 PM'
    }
  }

  return output
}

export default defineComponent({
  name: 'LocationCard',
  compnents: {
    VueCal
  },
  props: {
    id: {
      type: String,
      required: true
    },
    mode: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const id = ref(props.id)
    const mode = ref(props.mode)
    const data = ref<any>(null)
    const startHour = ref<null | number>(null)
    const endHour = ref<null | number>(null)
    const events = ref<null | []>(null)

    if (mode.value == 'hours') {
      fetch(`/api/locations/${id.value}`)
        .then((response) => response.json())
        .then((json) => {
          data.value = json
        })
    }

    if (mode.value == 'day') {
      fetch(`/api/ViewScheduling/GetCalendarFor/${id.value}?today=true`).then((response) =>
        response.json().then((json) => {
          events.value = json.events.map((event: any) => {
            return {
              id: event.id,
              title: event.title,
              start: new Date(event.start),
              end: new Date(event.end),
              location: json.location.buildingCode + ' - ' + json.location.room,
              description: event.description
            }
          })

          if (json.hours) {
            const today = new Date().getDay()
            startHour.value = json.hours.openTimes[today]
            endHour.value = json.hours.closeTimes[today]
          } else {
            startHour.value = 8
            endHour.value = 18
          }
          data.value = json.location
        })
      )
    }

    return {
      id,
      mode,
      data,
      startHour,
      endHour,
      events
    }
  },
  methods: {
    translateHours() {
      var output = []
      var days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      let i = 0
      if (!this.data.hours.weekends) {
        i = 1
      }
      let length = days.length
      if (!this.data.hours.weekends) {
        length--
      }
      for (; i < days.length; i++) {
        output.push(days[i] + ':')
        if (this.data.hours.openTimes[i] && this.data.hours.closeTimes[i]) {
          output.push(
            `${translateHour(this.data.hours.openTimes[i])} - ${translateHour(
              this.data.hours.closeTimes[i]
            )}`
          )
        } else {
          output.push(`Closed`)
        }
      }

      return output
    },
    openNewTab(url: string) {
      window.open(url, '_blank')
    },
    modalFor(event: any) {
      const tableMessage = {
        'Event Title': event.title,
        'Start Time': this.formattedTime(new Date(event.start)),
        'End Time': this.formattedTime(new Date(event.end)),
        Location: event.location,
        Description: event.description || 'No description'
      }

      launchModal({
        type: ModalType.Information,
        header: event.title,
        tableMessage: tableMessage,
        confirmButtonText: 'Ok'
      })
    },
    formattedTime(time: Date) {
      return time.toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      })
    }
  }
})
</script>

<template>
  <template v-if="data == null">
    <v-col style="display: flex; width: 100%; justify-content: center">
      <v-progress-circular indeterminate size="50"></v-progress-circular>
    </v-col>
  </template>
  <template v-else-if="mode == 'hours' && !data.hours">
    <v-card-title>Oops! Sorry.</v-card-title>
    <v-card-text>Hours could not be displayed</v-card-text>
  </template>
  <template v-else-if="mode == 'hours'">
    <v-card-title style="display: flex; align-items: center; justify-content: center">
      <v-icon
        icon="fa-calendar-days"
        @click="openNewTab('/calendar/' + id)"
        size="20px"
        style="margin-right: 15px"
        :aria-label="'View Calendar for ' + data.displayName"
      ></v-icon>
      {{ data.buildingCode }} - {{ data.room }}
    </v-card-title>
    <v-divider thickness="2px" length="100%"></v-divider>
    <v-card-text style="display: grid; grid-template-columns: 1fr 1fr">
      <template v-for="(t, index) in translateHours()">
        <div class="hours-days" v-if="index % 2 == 0">{{ t }}</div>
        <div class="hours-times" v-else>{{ t }}</div>
      </template>
    </v-card-text>
  </template>
  <template v-else-if="mode == 'day'">
    <v-card-title style="display: flex; align-items: center; justify-content: center">
      <v-icon
        icon="fa-calendar-days"
        @click="openNewTab('/calendar/' + id)"
        size="20px"
        style="margin-right: 15px"
      ></v-icon>
      {{ data.displayName }}
    </v-card-title>
    <v-divider thickness="2px" length="100%"></v-divider>
    <v-card-text v-if="startHour != null && endHour != null" style="height: 300px">
      <vue-cal
        ref="vuecal"
        :time-from="startHour * 60"
        :time-to="endHour * 60"
        :time-cell-height="30"
        :disable-views="['years', 'year', 'month', 'week']"
        :active-view="'day'"
        :transitions="false"
        show-week-number
        hide-view-selector
        :events="events"
        :twelve-hour="true"
        :on-event-click="(event: any) => modalFor(event)"
      >
        <template v-slot:no-event>
          <div></div>
        </template>
        <template v-slot:arrow-next>
          <div></div>
        </template>
        <template v-slot:arrow-prev>
          <div></div>
        </template>
        <template v-slot:title="{ title, view }">
          {{ title.split(' ').slice(1, 4).join(' ') }}
        </template>
      </vue-cal>
    </v-card-text>
  </template>
</template>

<style scoped>
.hours-days {
  font-weight: bold;
  text-align: right;
  margin-right: 10px;
}

.hours-times {
  margin-left: 10px;
}

:deep(.vuecal__event) {
  background-color: #3f51b5;
  color: white;
  cursor: pointer;
}
</style>
